import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "../app/routes.js";
import { HqApiError, authApi as api } from "../shared/api/auth.js";
import { useSession } from "../app/session-store.js";
import { intlLocaleOf, useLocale, useT } from "../i18n/index.js";

/**
 * Đổi mật khẩu tạm.
 *
 * Trang này KHÔNG phải một gợi ý: máy chủ trả `HQ_PASSWORD_CHANGE_REQUIRED` cho mọi đường khác cho
 * tới khi đổi xong. Giao diện chỉ đưa người dùng tới đúng chỗ — hàng rào nằm ở máy chủ, vì một trang
 * bị chuyển hướng vẫn gọi được bằng `curl`.
 */
export function ChangePasswordPage() {
  const t = useT("auth");
  const locale = intlLocaleOf(useLocale());
  const navigate = useNavigate();
  const session = useSession((s) => s.session);
  const setSession = useSession((s) => s.setSession);
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [xacNhan, setXacNhan] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dangGui, setDangGui] = useState(false);

  /**
   * Lệch nhau thì chặn ngay ở đây, KHÔNG gửi đi.
   *
   * Máy chủ không thể kiểm hộ: nó chỉ nhận một mật khẩu, và một lỗi gõ sẽ được đặt thành mật khẩu
   * thật. Người dùng khoá chính mình ra ngoài bằng một chuỗi họ không biết là chuỗi gì — và với tài
   * khoản chủ duy nhất, đường về là `db:reset-owner-credential` ở dòng lệnh.
   */
  const lechNhau = xacNhan.length > 0 && xacNhan !== newPassword;
  const guiDuoc = newPassword.length >= 12 && xacNhan === newPassword;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!guiDuoc) {
      setError(t("change.mismatchSubmit"));
      return;
    }
    setError(null);
    setDangGui(true);
    try {
      setSession(await api.changePassword({ currentPassword, newPassword }));
      navigate(ROUTES.attention, { replace: true });
    } catch (err) {
      setError(err instanceof HqApiError ? err.message : t("serverUnreachable"));
    } finally {
      setDangGui(false);
    }
  }

  const hanDung = session?.temporaryCredentialExpiresAt;

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <header>
        <h1 className="text-xl font-semibold">{t("change.title")}</h1>
        <p className="text-sm opacity-70">
          {t("change.intro")}
          {hanDung ? ` ${t("change.expiresAt", { at: new Date(hanDung).toLocaleString(locale) })}` : ""}
        </p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          {t("change.current")}
          <input
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            className="rounded border bg-background px-3 py-2"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          {t("change.new")}
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={newPassword}
            onChange={(e) => setNew(e.target.value)}
            className="rounded border bg-background px-3 py-2"
          />
          <span className="text-xs opacity-60">{t("change.minLength")}</span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          {t("change.confirm")}
          <input
            type="password"
            autoComplete="new-password"
            required
            value={xacNhan}
            onChange={(e) => setXacNhan(e.target.value)}
            aria-invalid={lechNhau}
            className={`rounded border bg-background px-3 py-2 ${lechNhau ? "border-red-400" : ""}`}
          />
          {/* Báo NGAY khi gõ, không đợi bấm Lưu — sửa một ô đang nhìn dễ hơn sửa sau một thông báo lỗi. */}
          {lechNhau ? <span className="text-xs text-red-600">{t("change.mismatch")}</span> : null}
        </label>

        {error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={dangGui || !guiDuoc}
          className="rounded bg-foreground px-3 py-2 text-background disabled:opacity-50"
        >
          {dangGui ? t("change.submitting") : t("change.submit")}
        </button>
      </form>
    </main>
  );
}
